import React, { Suspense, useEffect, useState } from 'react'
import './App.css'
import LeftSideBar from './components/LeftSideBar'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import Toolbar from '@material-ui/core/Toolbar'
import RightDrawer from './components/RightDrawer'
import { authService } from './utils/fbase'
import { onAuthStateChanged } from 'firebase/auth'
import Header from './components/header/Header'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import loadable from '@loadable/component'

import { User } from './types/User'

import {
  DISCLAIMER_URL,
  PRIVACY_URL,
  TERMS_URL,
} from './components/footer/Footer'
import { UserActions, useUser } from './redux/slices/userSlice'
import { useDispatch } from 'react-redux'
import Loading from './components/common/Loading'
import { Container } from '@material-ui/core'

const MemoEnd = loadable(() => import('./components/memo/MemoEnd'))
const MemoContainer = loadable(() => import('./components/memo/MemoContainer'))
const MemoCreation = loadable(() => import('./components/memo/MemoCreation'))
const MemoEdit = loadable(() => import('./components/memo/MemoEdit'))
const TodoContainer = loadable(() => import('./components/todo/TodoContainer'))
const KeepContainer = loadable(() => import('./components/keep/KeepContainer'))
const CalendarContainer = loadable(
  () => import('./components/calendar/CalendarContainer')
)
const Login = loadable(() => import('./components/login/Login'))
const Terms = loadable(() => import('./components/policy/Terms'))
const Privacy = loadable(() => import('./components/policy/Privacy'))
const Disclaimer = loadable(() => import('./components/policy/Disclaimer'))
const SuggestionList = loadable(
  () => import('./components/suggest/SuggestionList'),
  {
    fallback: <Loading />,
  }
)

import { useReduxLocation } from './redux/slices/locationSlice'

import usePwa from './hooks/usePwa'

import SuggestionCreation from './components/suggest/SuggestionCreation'
import SuggestionEnd from './components/suggest/SuggestionEnd'

const drawerWidth = 240

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
    },
    drawerPaper: {
      width: drawerWidth,
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    content: {
      padding: theme.spacing(1),
    },
  })
)
function App(): JSX.Element {
  usePwa()
  const classes = useStyles()
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [init, setInit] = useState<boolean>(false)

  const location = useLocation()
  const dispatch = useDispatch()
  const user: User | null | undefined = useUser()

  const history = useHistory()
  const reduxLocation = useReduxLocation()

  useEffect(() => {
    if (!reduxLocation.path) {
      return
    }
    history.push(reduxLocation.path)
  }, [reduxLocation])

  useEffect(() => {
    onAuthStateChanged(authService, (userFromAuth) => {
      if (userFromAuth) {
        const newVar = {
          id: userFromAuth.uid,
          photoUrl: userFromAuth.photoURL || '',
          name: userFromAuth.displayName || 'unknown',
        }
        dispatch(UserActions.set(newVar))
      } else {
        dispatch(UserActions.set(null))
      }
      setInit(true)
    })
  }, [])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  if (!init) {
    return <Loading />
  }

  if (location.pathname === TERMS_URL) {
    return <Terms user={user} />
  }

  if (location.pathname === PRIVACY_URL) {
    return <Privacy user={user} />
  }

  if (location.pathname === DISCLAIMER_URL) {
    return <Disclaimer user={user} />
  }

  if (!user) {
    return (
      <Switch>
        <Route path={'/'} exact={true}>
          <Login />
        </Route>
      </Switch>
    )
  }
  return (
    <div className={classes.root}>
      <Header
        showDrawer={true}
        handleDrawerToggle={handleDrawerToggle}
        user={user}
      />
      <LeftSideBar
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Container className={classes.content} maxWidth={'xl'}>
        <Toolbar />
        <Switch>
          <Route path={['/', '/todo']} exact={true}>
            <Suspense fallback={<Loading />}>
              <TodoContainer />
            </Suspense>
          </Route>
          <Route path={'/keep'} exact={true}>
            <Suspense fallback={<Loading />}>
              <KeepContainer />
            </Suspense>
          </Route>
          <Route path={'/calendar'} exact={true}>
            <Suspense fallback={<Loading />}>
              <CalendarContainer />
            </Suspense>
          </Route>
          <Route path={'/memo'} exact={true}>
            <Suspense fallback={<Loading />}>
              <MemoContainer />
            </Suspense>
          </Route>
          <Route path={'/memo/add'} exact={true}>
            <Suspense fallback={<Loading />}>
              <MemoCreation />
            </Suspense>
          </Route>
          <Route path={'/memo/edit/:id'} exact={true}>
            <Suspense fallback={<Loading />}>
              <MemoEdit />
            </Suspense>
          </Route>
          <Route path={'/memo/:id'}>
            <Suspense fallback={<Loading />}>
              <MemoEnd />
            </Suspense>
          </Route>
          <Route path={'/suggestion'} exact={true}>
            <Suspense fallback={<Loading />}>
              <SuggestionList />
            </Suspense>
          </Route>
          <Route path={'/suggestion/add'} exact={true}>
            <Suspense fallback={<Loading />}>
              <SuggestionCreation />
            </Suspense>
          </Route>
          <Route path={'/suggestion/:id'} exact={true}>
            <SuggestionEnd />
          </Route>
        </Switch>
      </Container>
      <RightDrawer />
    </div>
  )
}

export default App
